import { memo, type MouseEvent } from "react";
import type { InventoryItem, SlotLocation } from "../types";
import { SLOT_EQUIPMENT_TYPE } from "../equipmentConfig";
import { SlotBackground } from "./SlotBackground";
import { useDragDropContext } from "../context/DragDropContext";
import { useIs2K } from "../hooks/useIs2K";

interface Props {
  item?: InventoryItem | null;
  location?: SlotLocation;
  onClick?: (item: InventoryItem, location?: SlotLocation) => void;
  onRightClick?: (e: MouseEvent, item: InventoryItem, location?: SlotLocation) => void;
  isActive?: boolean;
  isUnavailable?: boolean;
  slotLabel?: string;
  slotIcon?: string;
  compact?: boolean;
}

function locationsEqual(a: SlotLocation, b: SlotLocation): boolean {
  if (a.section !== b.section) return false;
  if (a.section === 'equipment' && b.section === 'equipment') return a.key === b.key;
  if (a.section !== 'equipment' && b.section !== 'equipment') return a.index === b.index;
  return false;
}

function isDropValid(dragItem: InventoryItem, _from: SlotLocation, to: SlotLocation): boolean {
  if (to.section === 'equipment') {
    return dragItem.equipmentType === SLOT_EQUIPMENT_TYPE[to.key];
  }
  return true;
}

function propsEqual(prev: Props, next: Props): boolean {
  if (prev.item?.id !== next.item?.id) return false;
  if (prev.item?.quantity !== next.item?.quantity) return false;
  if (prev.isActive !== next.isActive) return false;
  if (prev.isUnavailable !== next.isUnavailable) return false;
  if (prev.slotLabel !== next.slotLabel) return false;
  if (prev.slotIcon !== next.slotIcon) return false;
  if (prev.compact !== next.compact) return false;
  if (prev.onClick !== next.onClick) return false;
  if (prev.onRightClick !== next.onRightClick) return false;
  const aLoc = prev.location;
  const bLoc = next.location;
  if (aLoc === bLoc) return true;
  if (!aLoc || !bLoc) return false;
  return locationsEqual(aLoc, bLoc);
}

export const ItemSlot = memo(function ItemSlot({
  item,
  location,
  onClick,
  onRightClick,
  isActive,
  isUnavailable,
  slotLabel,
  slotIcon,
  compact,
}: Props) {
  const { dragState, rejectedLocation, startDrag } = useDragDropContext();
  const is2K = useIs2K();

  const isDragSource =
    dragState.isDragging &&
    dragState.from !== null &&
    location !== undefined &&
    locationsEqual(dragState.from, location);

  const isValidDropTarget =
    dragState.isDragging &&
    !isUnavailable &&
    location !== undefined &&
    !isDragSource &&
    dragState.item !== null &&
    dragState.from !== null &&
    isDropValid(dragState.item, dragState.from, location);

  const isRejected =
    rejectedLocation !== null &&
    location !== undefined &&
    locationsEqual(rejectedLocation, location);

  const handleClick = (e: MouseEvent<HTMLDivElement>) => {
    e.stopPropagation();
    if (dragState.isDragging) return;
    if (item) onClick?.(item, location);
  };

  const handleContextMenu = (e: MouseEvent<HTMLDivElement>) => {
    e.stopPropagation();
    if (item && onRightClick) onRightClick(e, item, location);
  };

  const handlePointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    if (e.button !== 0 || isUnavailable || !item || !location) return;
    startDrag(item, location, e);
  };

  const slotState = isActive
    ? "active"
    : isUnavailable
      ? "unavailable"
      : item
        ? "with-item"
        : "empty";

  const imgMax = is2K ? { maxWidth: 64, maxHeight: 46 } : { maxWidth: 49, maxHeight: 35 };
  const iconMax = is2K ? { maxWidth: 44, maxHeight: 44 } : { maxWidth: 34, maxHeight: 34 };

  return (
    <div
      className={[
        "relative w-full h-full rounded-md overflow-hidden transition-colors",
        isUnavailable ? "cursor-default" : "cursor-pointer select-none",
      ].join(" ")}
      style={isDragSource ? { opacity: 0.35 } : undefined}
      data-slot-location={location && !isUnavailable ? JSON.stringify(location) : undefined}
      onClick={isUnavailable ? undefined : handleClick}
      onContextMenu={isUnavailable ? undefined : handleContextMenu}
      onPointerDown={handlePointerDown}
    >
      <SlotBackground state={slotState}>
        {!isUnavailable && (
          <div className="relative w-full h-full flex flex-col items-center justify-center">
            {item ? (
              <>
                {compact ? (
                  <span className="absolute top-1 right-1.5 text-[10px] 2560px:text-[13px] leading-none text-white/60 pointer-events-none">
                    x{item.quantity}
                  </span>
                ) : (
                  <div className="absolute top-0 left-0 right-0 flex items-center justify-between px-1 1440px:px-1.5 pt-1 1440px:pt-1.5 pointer-events-none">
                    <span className="text-[10px] 2560px:text-[13px] leading-none text-white/60">
                      {item.weight} kg
                    </span>
                    <span className="text-[10px] 2560px:text-[13px] leading-none text-white/60">
                      x{item.quantity}
                    </span>
                  </div>
                )}

                {item.image && (
                  <img
                    src={item.image}
                    alt={item.name}
                    className="object-contain pointer-events-none"
                    style={imgMax}
                    draggable={false}
                  />
                )}

                {!compact && (
                  <span className="absolute bottom-0 left-0 right-0 text-center text-[11px] 1440px:text-[13px] 2560px:text-[16px] leading-none text-white pb-1 1440px:pb-1.5 pointer-events-none truncate px-1">
                    {item.name}
                  </span>
                )}
              </>
            ) : slotIcon ? (
              <img
                src={slotIcon}
                alt=""
                className="object-contain pointer-events-none opacity-25"
                style={iconMax}
                draggable={false}
              />
            ) : slotLabel ? (
              <span className="absolute bottom-1.5 left-0 right-0 text-center text-[9px] 2560px:text-[12px] uppercase text-white/30 leading-none pointer-events-none">
                {slotLabel}
              </span>
            ) : null}
          </div>
        )}
      </SlotBackground>

      {isValidDropTarget && (
        <div
          className="absolute inset-0 rounded-md pointer-events-none"
          style={{
            boxShadow: 'inset 0 0 0 1px rgba(19,127,227,0.7)',
            background: 'rgba(19,127,227,0.08)',
          }}
        />
      )}

      {isRejected && (
        <div
          className="absolute inset-0 rounded-md pointer-events-none animate-pulse"
          style={{
            boxShadow: 'inset 0 0 0 1px rgba(255,40,40,0.8)',
            background: 'rgba(255,40,40,0.15)',
          }}
        />
      )}
    </div>
  );
}, propsEqual);
